import * as React from 'react'
import {
  Drawer, Box, Typography, IconButton, Button, TextField, MenuItem, InputAdornment, Alert,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

import {sectionCardSx, kpiLabelSx, primaryBtnSx, secondaryBtnSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER} from '../../../../styles/foundation/colors'
import {GAP} from '../../../../styles/foundation/spacing'

import {toTriple, withinTolerance} from '../weightMath'
import {int, kg} from '../format'
import {
  PACK_KIND, PACK_BRANDING, PACK_KIND_LABEL, PACK_BRANDING_LABEL,
  packUsage, mintPackId, volumetric,
} from './state/packingModel'
import CartonFigure from './components/CartonFigure'
import VolWorking from './components/VolWorking'

const labelSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}
const alertSx = {fontFamily: FONT, fontSize: SIZE['2xs'], py: 0.75, '& .MuiAlert-message': {lineHeight: 1.55}}
const fieldSx = {fontFamily: FONT, fontSize: SIZE.xs}

const DEFAULTS = {l: '30', b: '24', h: '12', selfWeightG: '120'}

/** Case-insensitive name clash across every OTHER material on the account. */
export function hasNameClash(packs, label, excludeId) {
  const needle = (label || '').trim().toLowerCase()
  return (packs || []).some((p) => p.id !== excludeId && (p.label || '').trim().toLowerCase() === needle)
}

/**
 * The fixed cascade-moves test. The brief's own sketch compares `nextVol`
 * and `pack.volKg` directly, which never fires for a kind switch AWAY from
 * carton (nextVol lands on `null`, so `nextVol != null` is false and the
 * comparison never even runs) — yet `UPSERT_PACK` in useFreezeStore.js
 * applies `merged.volKg || 0` to every dependent SKU/combo regardless, so a
 * carton-to-flyer edit silently rewrites their volumetric weight to zero
 * while this drawer would have reported "unchanged". Treating a `null` on
 * either side as the 0 the store actually applies closes that gap without
 * touching the tolerance rule itself.
 */
export function cascadeMoves(prevVolKg, nextVolKg) {
  const prev = prevVolKg == null ? 0 : prevVolKg
  const next = nextVolKg == null ? 0 : nextVolKg
  return !withinTolerance(next, prev)
}

/**
 * The drawer's before -> after sentence, built only from `weightMath`'s own
 * `kg`/`int` formatters and the `moves` flag above — never a second
 * rounding or comparison. `usage` is `packUsage`'s own `{skus, combos}`.
 */
export function cascadeNotice(usage, prevVolKg, nextVolKg, moves) {
  const prefix = `${int(usage.skus)} SKUs use this material, along with ${int(usage.combos)} observed combinations.`
  if (!moves) return `${prefix} Their volumetric weight is unchanged by this edit.`
  return `${prefix} Saving moves their volumetric weight from ${kg(prevVolKg)} to ${kg(nextVolKg)}, `
    + 'because volumetric weight follows the material.'
}

/**
 * The patch `onSave` receives — exactly the shape `useFreezeStore.js`'s own
 * `UPSERT_PACK` expects. `volKg` here is only ever a PREVIEW: the reducer
 * recomputes it itself from `dims` via the same `weightMath.volumetricKg`,
 * so the two can never disagree about what a carton's own dimensions imply.
 */
export function buildPackPatch({pack, packs, label, branding, kind, l, b, h, selfWeightG}) {
  const triple = [Number(l) || 0, Number(b) || 0, Number(h) || 0]
  const nextVol = kind === PACK_KIND.CARTON ? volumetric(triple) : null
  return {
    id: pack ? pack.id : mintPackId(packs, kind),
    label: (label || '').trim(),
    branding,
    kind,
    dims: kind === PACK_KIND.CARTON ? triple : null,
    selfWeightG: Number(selfWeightG) || 0,
    volKg: nextVol,
  }
}

/**
 * Add-or-edit drawer for one packing material — Packing Task 6. `pack` null
 * means add; `state` is the narrow slice (`skus`/`combos`/`packs`/...)
 * `PackingPage` threads down from the one module-wide store, read here only
 * to check the name clash and to look up `packUsage` for the cascade
 * warning below — never copied into local state and never mutated
 * directly; every actual write goes through `onSave` -> `actions.upsertPack`.
 *
 * THE CASCADE WARNING is why this drawer exists: a freeze is agreed
 * against the material, not the SKU, so editing a carton's dimensions
 * rewrites `volKg` (and therefore `chargeKg`) on every SKU and combo
 * carrying this `packId`. The before -> after figures below come from
 * `cascadeMoves`/`cascadeNotice` above, which lean on the shared
 * `weightMath` formatters and the model's own `volumetric()` gate — never
 * a second rounding or a bare `!==`.
 *
 * Keyboard-dismissable and focus-return are MUI's Modal defaults (Escape,
 * backdrop click, restore focus to the trigger); the caller
 * (`PackingPanel`) additionally falls back to the active rail item via
 * `focusFallbackRef` when that restore lands on `<body>` — the SAME
 * mechanism CataloguePage/SubcategoryPanel already use, reused rather than
 * invented a second time here.
 */
export default function PackDrawer({open, state, pack, onClose, onSave}) {
  const [label, setLabel] = React.useState('')
  const [branding, setBranding] = React.useState(PACK_BRANDING.SELF)
  const [kind, setKind] = React.useState(PACK_KIND.CARTON)
  const [l, setL] = React.useState(DEFAULTS.l)
  const [b, setB] = React.useState(DEFAULTS.b)
  const [h, setH] = React.useState(DEFAULTS.h)
  const [selfWeightG, setSelfWeightG] = React.useState(DEFAULTS.selfWeightG)

  const resetFields = (p) => {
    if (p) {
      setLabel(p.label)
      setBranding(p.branding)
      setKind(p.kind)
      const triple = toTriple(p.dims)
      setL(triple ? String(triple[0]) : DEFAULTS.l)
      setB(triple ? String(triple[1]) : DEFAULTS.b)
      setH(triple ? String(triple[2]) : DEFAULTS.h)
      setSelfWeightG(String(p.selfWeightG != null ? p.selfWeightG : 0))
    } else {
      setLabel('')
      setBranding(PACK_BRANDING.SELF)
      setKind(PACK_KIND.CARTON)
      setL(DEFAULTS.l)
      setB(DEFAULTS.b)
      setH(DEFAULTS.h)
      setSelfWeightG(DEFAULTS.selfWeightG)
    }
  }

  // Adjusted-during-render reset on the closed -> open transition, the same
  // pattern MergeDrawer/SubcategoryDrawer use, so a stale draft never
  // carries over from the last time this one drawer instance was shown for
  // a different material (or for "add" after an "edit").
  const [prevOpen, setPrevOpen] = React.useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) resetFields(pack)
  }

  const triple = [Number(l) || 0, Number(b) || 0, Number(h) || 0]
  const dimsInvalid = kind === PACK_KIND.CARTON && (triple[0] <= 0 || triple[1] <= 0 || triple[2] <= 0)

  const trimmedLabel = label.trim()
  const nameClash = hasNameClash(state.packs, trimmedLabel, pack ? pack.id : null)
  const nameReady = trimmedLabel.length >= 2 && !nameClash
  const dimsReady = kind !== PACK_KIND.CARTON || !dimsInvalid
  const weightNum = Number(selfWeightG)
  const weightReady = selfWeightG !== '' && Number.isFinite(weightNum) && weightNum >= 0
  const ready = nameReady && dimsReady && weightReady

  const usage = pack ? packUsage(state, pack.id) : {skus: 0, combos: 0}
  const totalUsage = usage.skus + usage.combos
  const nextVol = kind === PACK_KIND.CARTON ? volumetric(triple) : null
  const moves = !!pack && cascadeMoves(pack.volKg, nextVol)

  const handleSubmit = () => {
    if (!ready) return
    const patch = buildPackPatch({pack, packs: state.packs, label, branding, kind, l, b, h, selfWeightG})
    onSave(patch, !pack)
  }

  return (
    <Drawer anchor='right' open={open} onClose={onClose} sx={{'& .MuiDrawer-paper': {width: {xs: '100%', sm: 720}}}}>
      <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
        <Box sx={{p: 2, borderBottom: `1px solid ${BORDER}`}}>
          <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1}}>
            <Box sx={{minWidth: 0}}>
              <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.md, color: NAVY}}>
                {pack ? 'Edit packing material' : 'Add packing material'}
              </Typography>
              {pack && <Typography sx={labelSx}>{pack.label} · {pack.id}</Typography>}
            </Box>
            <IconButton onClick={onClose} aria-label='Close packing material drawer' size='small'>
              <CloseIcon fontSize='small' />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{p: 2, overflowY: 'auto', flex: 1, display: 'grid', gap: GAP.md, alignContent: 'start'}}>
          <Alert severity='warning' sx={alertSx}>
            Volumetric weight is anchored to the packing material, not to the product. A freeze holds only
            while the declared material is the one actually used.
          </Alert>

          <TextField
            size='small'
            label='Material name'
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            error={nameClash}
            helperText={nameClash ? 'This account already has a material with that name.' : ' '}
            inputProps={{'aria-invalid': nameClash}}
            sx={fieldSx}
          />

          <TextField
            select
            size='small'
            label='Branding'
            value={branding}
            onChange={(e) => setBranding(e.target.value)}
            helperText='Self-branded material is printed for this account.'
            sx={fieldSx}
          >
            {Object.entries(PACK_BRANDING_LABEL).map(([value, text]) => (
              <MenuItem key={value} value={value}>{text}</MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size='small'
            label='Type'
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            helperText='Only a carton carries dimensions, so only a carton implies a volumetric weight.'
            sx={fieldSx}
          >
            {Object.entries(PACK_KIND_LABEL).map(([value, text]) => (
              <MenuItem key={value} value={value}>{text}</MenuItem>
            ))}
          </TextField>

          {kind === PACK_KIND.CARTON ? (
            <Box sx={{...sectionCardSx, display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}, gap: GAP.md}}>
              <Box sx={{display: 'grid', gap: GAP.sm, alignContent: 'start'}}>
                <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: GAP.sm}}>
                  <TextField
                    size='small' type='number' label='Length (cm)' value={l}
                    onChange={(e) => setL(e.target.value)}
                    inputProps={{min: 1, step: 1}} sx={fieldSx}
                  />
                  <TextField
                    size='small' type='number' label='Breadth (cm)' value={b}
                    onChange={(e) => setB(e.target.value)}
                    inputProps={{min: 1, step: 1}} sx={fieldSx}
                  />
                  <TextField
                    size='small' type='number' label='Height (cm)' value={h}
                    onChange={(e) => setH(e.target.value)}
                    inputProps={{min: 1, step: 1}} sx={fieldSx}
                  />
                </Box>
                <VolWorking dims={triple} />
                {dimsInvalid && (
                  <Alert severity='warning' sx={alertSx}>
                    Length, breadth and height must each be at least 1 cm before this material can be saved.
                  </Alert>
                )}
              </Box>

              {/* Deliberately NOT captioned "Approximate carton" — that wording
                  belongs to components/CartonSVG.js's shape-only variant. Pairing
                  it with CartonFigure (the measured, to-scale drawing used here)
                  would contradict the caption CartonFigure renders underneath its
                  own drawing, which says outright that the outline tracks the
                  numbers just typed — see that file's own header comment. */}
              <Box sx={{display: 'grid', gap: 0.5, alignContent: 'start', justifyItems: 'center'}}>
                <Typography sx={kpiLabelSx}>Carton</Typography>
                <CartonFigure dims={triple} />
              </Box>
            </Box>
          ) : (
            <Alert severity='info' sx={alertSx}>
              {kind === PACK_KIND.FLYER ? 'A flyer' : 'A material of this type'} has no fixed dimensions, so
              it implies no volumetric weight. Shipments packed in it are governed by dead weight, and the
              volumetric figure on each SKU stays whatever the base data table says.
            </Alert>
          )}

          <TextField
            size='small'
            type='number'
            label='Material self-weight'
            value={selfWeightG}
            onChange={(e) => setSelfWeightG(e.target.value)}
            error={!weightReady}
            helperText='The empty material on its own. It is added to the dead weight of the goods inside.'
            InputProps={{endAdornment: <InputAdornment position='end'>g</InputAdornment>}}
            inputProps={{min: 0, step: 1, 'aria-label': 'Material self-weight in grams'}}
            sx={fieldSx}
          />

          {pack && totalUsage > 0 && (
            <Alert severity={moves ? 'warning' : 'info'} sx={alertSx}>
              {cascadeNotice(usage, pack.volKg, nextVol, moves)}
            </Alert>
          )}
        </Box>

        <Box sx={{p: 1.5, borderTop: `1px solid ${BORDER}`, display: 'flex', gap: 1, justifyContent: 'flex-end'}}>
          <Button sx={secondaryBtnSx} onClick={onClose}>Cancel</Button>
          <Button sx={primaryBtnSx} disabled={!ready} onClick={handleSubmit}>
            {pack ? 'Save changes' : 'Add material'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  )
}
